import { computed, ref, watch } from 'vue'
import { DropDownMenuItemProps } from 'frontcore/views/drop-down/domain/types'
import { Nullable } from 'frontcore/presentation/types'

interface Props {
  disabled?: boolean
  error?: string
  value?: Nullable<DropDownMenuItemProps['id']> | DropDownMenuItemProps['id'][]
  nullable?: boolean
}

const useSelectState = (props: Props) => {
  const isFocused = ref(false)

  const toggleFocus = () => {
    if (props.disabled) { return }
    isFocused.value = !isFocused.value
  }

  const clickOutside = () => {
    if (!isFocused.value) { return }
    toggleFocus()
  }

  watch(
    () => props.disabled,
    (disabled) => {
      if (!disabled) { return }
      isFocused.value = false
    }
  )

  const hasSelectedValues = computed<boolean>(() => {
    const value = props.value
    if (Array.isArray(value)) {
      return value.length > 0
    }
    return value != null && value !== ''
  })

  const showInput = computed(() => {
    if (props.disabled) { return false }
    return isFocused.value || !hasSelectedValues.value
  })

  const chevronStyle = computed(() => ({ transform: `rotate(${ isFocused.value ? 180 : 0}deg)`}))

  const state = computed(() => {
    if (props.disabled) { return 'disabled'}
    else if (props.error) { return 'error'}
    else if (isFocused.value) { return 'focus'}
    return 'default'
  })

  const stateStyleClass = computed(() => `input-text__state-${state.value}`)

  const showClearButton = computed(() => {
    return !isFocused.value && hasSelectedValues.value && props.nullable && !props.disabled
  })

  return {
    chevronStyle,
    clickOutside,
    isFocused,
    showClearButton,
    showInput,
    stateStyleClass,
    toggleFocus,
  }
}

export default useSelectState
